import DestinationItem from "~/components/blocks/common/DestinationItem";
import { fetchLocation } from "~/lib/helpers/googleLocationDataManager";
import { History, LocateFixed, Building, Search, MapPin } from "lucide-react";
// import { locations } from "../Header/NavItems/constant";
import SearchSkeletonLoader from "./SearchSkeletonLoader";

interface RecentSearchesProps {
  address: string;
  loading: boolean;
  suggestions: readonly {
    placeId: string;
    description: string;
    /** Short place name (curated DB rows only) — preferred as the label. */
    name?: string;
    type?: "place" | "provider" | "property";
    slug?: string;
  }[];
  getSuggestionItemProps: any;
  recentSearches: {
    title: string;
    description?: string | null;
    urlString?: string;
  }[];
  handleSelectLocation: (
    location: string,
    coords: { lat: number; lng: number } | null,
  ) => void;
  /** Nearby / current-location — prefer over fetchLocation+handleSelectLocation. */
  onSelectNearby?: () => void;
  handleSelectWithPlaceId?: (
    location: string,
    placeId: string,
  ) => void;
  handleSelectRecentSearch?: (
    title: string,
    urlString: string,
  ) => void;
  onFreeTextSearch?: (rawText: string) => void;
  /** When false, property / office-name rows are hidden (default true). */
  showOfficeNames?: boolean;
}

export default function RecentSearches({
  address,
  loading,
  suggestions = [],
  getSuggestionItemProps,
  recentSearches,
  handleSelectLocation,
  onSelectNearby,
  handleSelectWithPlaceId,
  handleSelectRecentSearch,
  onFreeTextSearch,
  showOfficeNames = true,
}: RecentSearchesProps) {
  const trimmedAddress = address.trim();
  return (
    <div>
      {!address && (
        <div className=''>
          <DestinationItem
            title='Nearby'
            description='Search Nearby Offices'
            // User current location need to add this API in nest.js @faheelKhan
            onClick={() =>
              onSelectNearby
                ? onSelectNearby()
                : fetchLocation(handleSelectLocation)
            }
            icon={<LocateFixed className='text-blue-800' size={20} />}
          />
          {recentSearches.map((search) => (
            <DestinationItem
              key={`recent-${search.title}`}
              title={search.title}
              description={search.description ?? "Recent search"}
              onClick={() => {
                if (search.urlString && handleSelectRecentSearch) {
                  handleSelectRecentSearch(search.title, search.urlString);
                  return;
                }
                handleSelectLocation(search.title, null);
              }}
              icon={<History className='text-slate' size={20} />}
            />
          ))}
          {/* Suggested locations commented for now */}
          {/* 
           <div className='flex items-center gap-2 mt-1'>
            <span className='text-xs text-gray-800'>Suggested Locations</span>
          </div>
          {locations.map((location) => (
            <DestinationItem
              key={`location-${location.title}`}
              title={location.title}
              description={location.description}
              onClick={() => handleSelectLocation(location.title, null)}
              icon={<MapPin className='text-slate' size={20} />}
            />
          ))} */}
        </div>
      )}

      {loading && <SearchSkeletonLoader rows={5} />}

      {!loading &&
        (() => {
          // cap each section at 5 rows
          const cap = (key: string) =>
            suggestions.filter((s) => (s.type ?? "place") === key).slice(0, 5);
          const placeItems = cap("place");
          const propertyItems = showOfficeNames ? cap("property") : [];
          const canOfferFreeText =
            !!onFreeTextSearch && trimmedAddress.length >= 2;
          const showFreeTextRow = showOfficeNames && canOfferFreeText;

          if (!placeItems.length && !propertyItems.length && !showFreeTextRow) {
            return address ? (
              <div className='py-4 text-center text-sm text-slate'>
                No matching places or properties
              </div>
            ) : null;
          }

          // Every row (location / free-text / property) shares this shape so
          // the spacing is identical across the lists.
          const row = (
            suggestion: (typeof suggestions)[number],
            iconNode: React.ReactNode,
          ) => (
            <div
              {...getSuggestionItemProps(suggestion)}
              key={suggestion.placeId}
            >
              <DestinationItem
                title={suggestion.name || suggestion.description}
                highlight={trimmedAddress}
                iconWrapperClassName='shrink-0 p-1.5'
                icon={iconNode}
                onClick={() => {
                  // Use Place ID if available, otherwise fall back to regular selection
                  if (handleSelectWithPlaceId && suggestion.placeId) {
                    handleSelectWithPlaceId(
                      suggestion.description,
                      suggestion.placeId,
                    );
                  } else {
                    handleSelectLocation(suggestion.description, null);
                  }
                }}
              />
            </div>
          );

          // Grey outline map-pin (lucide MapPin, gray-600) — matches the grey
          // Building icon used for properties and the pin used across the cards.
          const pin = <MapPin className='shrink-0 text-gray-600' size={17} />;

          // Sectioned like the reference: a "Location" heading over the place
          // suggestions, an "Office Names" heading over the free-text search +
          // property results. The headings separate the sections (no divider).
          const heading = (label: string) => (
            <div className='px-1 pb-1 pt-2 text-xs font-medium text-gray-400'>
              {label}
            </div>
          );
          const showOfficeNamesSection =
            showOfficeNames && (showFreeTextRow || propertyItems.length > 0);

          return (
            <>
              {placeItems.length > 0 && (
                <div>
                  {heading("Location")}
                  {placeItems.map((s) => row(s, pin))}
                </div>
              )}

              {showOfficeNamesSection && (
                <div>
                  {heading("Office Names")}
                  {showFreeTextRow && (
                    <div
                      className='cursor-pointer'
                      onMouseDown={(event) => {
                        event.preventDefault();
                        onFreeTextSearch!(trimmedAddress);
                      }}
                    >
                      <DestinationItem
                        title={`Search for "${trimmedAddress}"`}
                        highlight={trimmedAddress}
                        iconWrapperClassName='shrink-0 p-1.5'
                        icon={<Search className='text-slate-600' size={17} />}
                      />
                    </div>
                  )}
                  {propertyItems.map((s) =>
                    row(s, <Building className='text-gray-600' size={17} />),
                  )}
                </div>
              )}
            </>
          );
        })()}
    </div>
  );
}
